import { catalogIdFromBackendKey } from "./backendModuleKeys";
import { logger } from "./logger";

export type ModuleCatalogEntry = {
  grants: readonly string[];
  requestable: boolean;
  read_endpoints: readonly string[];
  /** Extra permission codes co-granted on approval (backend `requires`, e.g. apartments.view_all). */
  requires?: readonly string[];
};

export type ModuleCatalog = Record<string, ModuleCatalogEntry>;

/**
 * Static fallback aligned with familyhub-backend #172 MODULE_REGISTRY (50dcf9c).
 * Used when GET /access-requests/modules/ is missing or unavailable.
 */
export const STATIC_MODULE_CATALOG: ModuleCatalog = {
  apartments: {
    grants: ["apartments.view_all"],
    requestable: true,
    read_endpoints: [],
  },
  payments: {
    grants: ["payments.view_all"],
    requestable: true,
    read_endpoints: [],
  },
  budget: {
    grants: ["budget.view_full", "budget.view_summary"],
    requestable: true,
    read_endpoints: [],
  },
  documents: {
    grants: ["documents.view_all"],
    requestable: true,
    read_endpoints: [],
  },
  tasks: {
    grants: ["tasks.view_all"],
    requestable: true,
    read_endpoints: [],
  },
  reminders: {
    grants: ["reminders.view"],
    requestable: true,
    read_endpoints: [],
  },
  knowledge: {
    grants: ["knowledge.view_family"],
    requestable: true,
    read_endpoints: [],
  },
  family: {
    grants: ["documents.view_family"],
    requestable: true,
    read_endpoints: [],
  },
  ai_chat: {
    grants: ["ai.view_status"],
    requestable: true,
    read_endpoints: [],
  },
  integrations: {
    grants: ["integrations.manage"],
    requestable: false,
    read_endpoints: [],
  },
};

export function normalizeCatalogModuleKey(key: string): string {
  return catalogIdFromBackendKey(key);
}

function normalizeCatalogEntry(raw: unknown): ModuleCatalogEntry | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const grants = Array.isArray(row.grants)
    ? row.grants.filter((g): g is string => typeof g === "string")
    : [];
  const readEndpoints = Array.isArray(row.read_endpoints)
    ? row.read_endpoints.filter((e): e is string => typeof e === "string")
    : [];
  const requires = normalizeRequiresField(row.requires);
  const entry: ModuleCatalogEntry = {
    grants,
    requestable: row.requestable === true,
    read_endpoints: readEndpoints,
  };
  if (requires && requires.length > 0) {
    entry.requires = requires;
  }
  return entry;
}

function normalizeRequiresField(raw: unknown): readonly string[] | undefined {
  if (raw == null) {
    return undefined;
  }
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const strings = raw.filter((item): item is string => typeof item === "string" && item.length > 0);
  return strings.length > 0 ? strings : undefined;
}

function resolveModuleListRowKey(row: Record<string, unknown>): string | null {
  const candidate = row.key ?? row.module_key ?? row.module;
  if (typeof candidate !== "string") {
    return null;
  }
  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function catalogFromModuleList(rows: unknown[]): ModuleCatalog | null {
  if (rows.length === 0) {
    return {};
  }
  const catalog: ModuleCatalog = {};
  for (const raw of rows) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const row = raw as Record<string, unknown>;
    const key = resolveModuleListRowKey(row);
    if (!key) {
      continue;
    }
    const entry = normalizeCatalogEntry(row);
    if (!entry) {
      continue;
    }
    catalog[normalizeCatalogModuleKey(key)] = entry;
  }
  return Object.keys(catalog).length > 0 ? catalog : null;
}

function catalogFromObjectMap(source: Record<string, unknown>): ModuleCatalog {
  const catalog: ModuleCatalog = {};
  for (const [key, value] of Object.entries(source)) {
    const normalizedKey = normalizeCatalogModuleKey(key);
    const entry = normalizeCatalogEntry(value);
    if (entry) {
      catalog[normalizedKey] = entry;
    }
  }
  return catalog;
}

export class ModuleCatalogParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ModuleCatalogParseError";
  }
}

/** Parse GET /access-requests/modules/ payload (list, paginated list, or object map). */
export function parseModuleCatalogResponse(data: unknown): ModuleCatalog {
  if (data == null) {
    throw new ModuleCatalogParseError("Module catalog response was empty");
  }

  if (Array.isArray(data)) {
    const catalog = catalogFromModuleList(data);
    if (!catalog) {
      throw new ModuleCatalogParseError("Module catalog list rows were invalid");
    }
    return catalog;
  }

  if (typeof data !== "object") {
    throw new ModuleCatalogParseError("Module catalog response was not JSON object");
  }

  const root = data as Record<string, unknown>;

  if ("modules" in root) {
    const modules = root.modules;
    if (Array.isArray(modules)) {
      const catalog = catalogFromModuleList(modules);
      if (!catalog) {
        throw new ModuleCatalogParseError("Module catalog modules[] rows were invalid");
      }
      return catalog;
    }
    if (modules && typeof modules === "object" && !Array.isArray(modules)) {
      const page = modules as { results?: unknown };
      if (Array.isArray(page.results)) {
        const catalog = catalogFromModuleList(page.results);
        if (!catalog) {
          throw new ModuleCatalogParseError("Module catalog paginated results were invalid");
        }
        return catalog;
      }
      const mapCatalog = catalogFromObjectMap(modules as Record<string, unknown>);
      if (Object.keys(mapCatalog).length > 0) {
        return mapCatalog;
      }
    }
  }

  const mapCatalog = catalogFromObjectMap(root);
  if (Object.keys(mapCatalog).length > 0) {
    return mapCatalog;
  }

  throw new ModuleCatalogParseError("Module catalog response shape was not recognized");
}

let activeCatalog: ModuleCatalog = { ...STATIC_MODULE_CATALOG };

export function getModuleCatalog(): ModuleCatalog {
  return activeCatalog;
}

export function setModuleCatalog(catalog: ModuleCatalog): void {
  activeCatalog = catalog;
}

export function getCatalogEntry(moduleKey: string): ModuleCatalogEntry | undefined {
  const normalized = normalizeCatalogModuleKey(moduleKey);
  return activeCatalog[normalized] ?? activeCatalog[moduleKey];
}

/** True when the live catalog marks the module requestable (defaults true if missing). */
export function isCatalogModuleRequestable(moduleKey: string): boolean {
  const entry = getCatalogEntry(moduleKey);
  if (!entry) {
    return true;
  }
  return entry.requestable;
}

export function isCatalogFetchFallbackError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return true;
  }
  const status = (error as { response?: { status?: number } }).response?.status;
  if (status === 401 || status === 404 || status === 501) {
    return true;
  }
  const code = (error as { code?: string }).code;
  return code === "ERR_NETWORK";
}

export function logModuleCatalogFallback(reason: string, detail?: unknown): void {
  logger.info("moduleCatalog", reason, detail);
}
